import React, { useState, useEffect, useRef } from 'react';
import { type BaseRecord, useInvalidate, useOne } from '@refinedev/core';
import { SmartDataTableProps } from './types';
import { EditComponent } from './EditComponent';
import { DeleteComponent } from './DeleteComponent';
import { TableComponent } from './TableComponent';
export { ExportComponent } from './ExportComponent';
export { MobileCardComponent } from './MobileCardComponent';
export { MobileHeaderComponent } from './MobileHeaderComponent';
export { MobilePaginationComponent } from './MobilePaginationComponent';

export const SmartDataTable: React.FC<SmartDataTableProps> = ({
  api,
  title,
  columns,
  search = { enabled: false, fields: [] },
  createForm,
  createConfigs,
  editForm,
  dynamicCreateForm,
  dynamicEditForm,
  formDisplayMode = 'drawer',
  drawerWidth,
  modalWidth,
  showCreate = true,
  showEdit = true,
  showDelete = true,
  showView = false,
  isFilter = true,
  actionWidth,
  customActions = [],
  onCreateSuccess,
  onEditSuccess,
  onDeleteSuccess,
  onView,
  onRowClick,
  onCreateDrawerClose,
  onEditClick,
  pageSize,
  pageOptions,
  showSizeChanger = true,
  showQuickJumper = true,
  showExport = false,
  requireFiltersForExport = false,
  showColumnVisibility = true,
  exportFormats,
  scroll,
  rowKey = 'id',
  style,
  className,
  cardProps,
  tableProps,
  // Chat configuration
  enableChat,
  chatType,
  chatRelativeId,
  chatPatientId,
  chatTitle,
  chatPlaceholder,
  // View switching configuration
  viewTabs,
  onViewChange,
  // Auto-open create configuration
  autoOpenCreate = false,
  autoOpenCreateKey,
  // Auto-open edit configuration
  autoOpenEditId = null,
  isintialScroll = false,
  // Mobile card configuration
  mobileCardComponent,
}) => {
  const invalidate = useInvalidate();
  // State
  const [editFormVisible, setEditFormVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BaseRecord | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<BaseRecord | null>(null);
  const [currentCreateResource, setCurrentCreateResource] = useState<string>(
    api.resource
  );
  const hasAutoOpenedEdit = useRef(false);

  // Fetch the record to edit when autoOpenEditId is provided
  const { data: autoEditRecord, isLoading: autoEditLoading } = useOne({
    resource: api.resource,
    id: autoOpenEditId || '',
    queryOptions: {
      enabled: !!autoOpenEditId && !hasAutoOpenedEdit.current,
    },
  });

  // Auto-open edit form when record is fetched
  useEffect(() => {
    if (autoEditRecord?.data && autoOpenEditId && !hasAutoOpenedEdit.current) {
      const record = autoEditRecord.data;
      setSelectedRecord(record);
      setEditFormVisible(true);
      onEditClick?.(record);
      hasAutoOpenedEdit.current = true;

      // Clear the autoOpenEditId after opening
      setTimeout(() => {
        hasAutoOpenedEdit.current = false;
      }, 1000);
    }
  }, [autoEditRecord, autoOpenEditId, onEditClick]);

  // Ensure columns is always an array
  const safeColumns = columns || [];

  // Handle edit form close
  const handleEditClose = () => {
    setEditFormVisible(false);
    setSelectedRecord(null);
  };

  // Handle create success
  const handleCreateSuccess = (data: any) => {
    // Invalidate the table data to refresh it with the new record
    invalidate({
      resource: currentCreateResource,
      invalidates: ['list'],
    });
    onCreateSuccess?.(data);
  };

  // Handle edit success
  const handleEditSuccess = (data: any) => {
    // Determine which resource to invalidate based on the edited record
    const resourceToInvalidate =
      dynamicEditForm?.getResource(data) || api.resource;

    // Invalidate the table data to refresh it with the updated record
    invalidate({
      resource: resourceToInvalidate,
      invalidates: ['list'],
    });
    onEditSuccess?.(data);
  };

  // Handle delete success
  const handleDeleteSuccess = (record: BaseRecord) => {
    // Determine which resource to invalidate based on the deleted record
    const resourceToInvalidate =
      dynamicEditForm?.getResource(record) || api.resource;

    // Invalidate the table data to refresh it after deletion
    invalidate({
      resource: resourceToInvalidate,
      invalidates: ['list'],
    });
    onDeleteSuccess?.(record);
    setRecordToDelete(null);
  };

  // Handle edit click
  const handleEditClick = (record: BaseRecord) => {
    setSelectedRecord(record);
    setEditFormVisible(true);
    onEditClick?.(record);
  };

  // Handle create resource change
  const handleCreateResourceChange = (resource: string) => {
    setCurrentCreateResource(resource);
  };

  // Handle delete click
  const handleDeleteClick = (record: BaseRecord) => {
    setRecordToDelete(record);
  };

  // Handle view click
  const handleViewClick = (record: BaseRecord) => {
    if (onView) {
      onView(record);
    } else {
      setSelectedRecord(record);
    }
  };

  const resolvedEditForm =
    editFormVisible && selectedRecord
      ? dynamicEditForm?.getForm(selectedRecord) || editForm
      : editForm;

  return (
    <>
      {/* Table Component */}
      <TableComponent
        api={api}
        title={title}
        columns={safeColumns}
        search={search}
        showCreate={showCreate}
        showEdit={showEdit}
        showDelete={showDelete}
        showView={showView}
        isFilter={isFilter}
        actionWidth={actionWidth}
        customActions={customActions}
        pageSize={pageSize}
        pageOptions={pageOptions}
        showSizeChanger={showSizeChanger}
        showQuickJumper={showQuickJumper}
        showExport={showExport}
        requireFiltersForExport={requireFiltersForExport}
        showColumnVisibility={showColumnVisibility}
        exportFormats={exportFormats}
        scroll={scroll}
        rowKey={rowKey}
        createForm={createForm}
        createConfigs={createConfigs}
        dynamicCreateForm={dynamicCreateForm}
        formDisplayMode={formDisplayMode}
        drawerWidth={drawerWidth}
        modalWidth={modalWidth}
        onCreateSuccess={handleCreateSuccess}
        onCreateResourceChange={handleCreateResourceChange}
        style={style}
        className={className}
        cardProps={cardProps}
        tableProps={tableProps}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onViewClick={handleViewClick}
        onRowClick={onRowClick || handleEditClick}
        viewTabs={viewTabs}
        onViewChange={onViewChange}
        autoOpenCreate={autoOpenCreate}
        autoOpenCreateKey={autoOpenCreateKey}
        autoOpenEditId={autoOpenEditId}
        onCreateDrawerClose={onCreateDrawerClose}
        mobileCardComponent={mobileCardComponent}
      />

      {/* Edit Component */}
      {editFormVisible && (editForm || dynamicEditForm) && resolvedEditForm && (
        <EditComponent
          api={{
            ...api,
            resource:
              dynamicEditForm?.getResource(selectedRecord || {}) ||
              api.resource,
          }}
          editForm={resolvedEditForm}
          formDisplayMode={formDisplayMode}
          visible={editFormVisible}
          selectedRecord={selectedRecord}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
          rowKey={rowKey}
          drawerWidth={drawerWidth}
          modalWidth={modalWidth}
          enableChat={enableChat}
          chatType={chatType}
          chatRelativeId={chatRelativeId || selectedRecord?.[rowKey]}
          chatPatientId={chatPatientId}
          chatTitle={chatTitle}
          chatPlaceholder={chatPlaceholder}
          customTitle={resolvedEditForm?.customTitle}
          isintialScroll={isintialScroll}
        />
      )}

      {/* Delete Component */}
      <DeleteComponent
        api={{
          ...api,
          resource:
            dynamicEditForm?.getResource(recordToDelete) || api.resource,
        }}
        record={recordToDelete}
        rowKey={rowKey}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
};
